import { Section, Reveal, Overline } from "./primitives";
import { PRICING, TEAM, INSIGHTS } from "../../data";
import { motion } from "framer-motion";
import { Check, Linkedin, ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { scrollToId } from "./primitives";

export function Pricing() {
  return (
    <Section id="pricing">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>Engagements</Overline>
          <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl tracking-tight">
            Plans that scale with <span className="text-gradient">your goals</span>.
          </h2>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 items-stretch">
        {PRICING.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1}>
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className={`relative h-full rounded-3xl p-8 flex flex-col shimmer-card ${p.featured ? "bg-brand-gradient text-white border-0 shadow-2xl shadow-primary/30" : "border border-border/10 bg-card"}`}
            >
              {p.featured && (
                <span className="absolute top-6 right-6 rounded-full bg-white/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">Most Popular</span>
              )}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl font-extrabold">{p.price}</span>
                <span className={`mb-1 text-sm ${p.featured ? "text-white/80" : "text-muted-foreground"}`}>{p.period}</span>
              </div>
              <p className={`mt-3 text-sm ${p.featured ? "text-white/85" : "text-muted-foreground"}`}>{p.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm">
                    <Check className={`h-4 w-4 shrink-0 ${p.featured ? "text-white" : "text-primary"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                onClick={() => scrollToId("#contact")}
                className={`mt-8 rounded-full w-full ${p.featured ? "bg-white text-primary hover:bg-white/90" : "bg-brand-gradient text-white border-0 hover:opacity-90"}`}
              >
                {p.name === "Enterprise" ? "Talk to Sales" : "Get Started"}
              </Button>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

export function Team() {
  return (
    <Section id="team" className="noise-bg">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Overline>The People</Overline>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl tracking-tight">
              Senior minds, <span className="text-gradient">hands-on</span>.
            </h2>
          </div>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={(i % 4) * 0.08}>
            <div className="group relative rounded-3xl overflow-hidden border border-border/10">
              <img src={m.img} alt={m.name} className="w-full h-72 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
                <div>
                  <div className="font-display font-bold text-white">{m.name}</div>
                  <div className="text-xs text-white/70">{m.role}</div>
                </div>
                <a href="#team" onClick={(e) => e.preventDefault()} className="h-9 w-9 grid place-items-center rounded-full bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-brand-gradient transition-colors">
                  <Linkedin className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

export function Insights() {
  return (
    <Section id="insights">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Overline>Insights</Overline>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl tracking-tight">
              Ideas worth <span className="text-gradient">stealing</span>.
            </h2>
          </div>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {INSIGHTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <motion.article whileHover={{ y: -6 }} className="group h-full rounded-3xl border border-border/10 bg-card overflow-hidden cursor-pointer">
              <div className="overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="text-primary font-semibold uppercase tracking-wider">{p.cat}</span>
                  <span>•</span>
                  <span>{p.read}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-gradient transition-colors">{p.title}</h3>
                <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-primary">
                  Read article <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </motion.article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
